/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useContext, useEffect, useState }  from 'react';
import Grid from '@material-ui/core/Grid';
import {
    object, string, shape, arrayOf, number, bool, array, func,
} from 'prop-types';
import { format } from 'date-fns';
import { compose } from 'recompose';
import { withStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import ReactHtmlParser from 'react-html-parser';
import Media from 'react-media';
import { transformCharacters } from '../../../../../../../helpers/markdown/characterTransformer';
import { dateStringReformat } from '../../commonProductPageHelper';
import { getBaseCode } from '../../../../../../../helpers/tracking/product/dataLayerProductHelpers';
import { getSSRDeviceType } from '../../../../../../../../state/ducks/App/App-Selectors';
import ProductReview from '../../../ProductReview';
import KlarnaCreditPromotionBadge from '../../../../../../Checkout/KlarnaPaymentOnSiteMessage/KlarnaCreditPromotionBadge/KlarnaCreditPromotionBadge';
import { CrossMerch } from '../../../../../CrossMerchContainer/CrossMerchContainer';
import useIsPlaPage from '../../../../../../../helpers/hooks/useIsPlaPage';
import { getFeatureFlag } from '../../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';

const styles = (theme) => ({
    crossedOutRetailPrice: {
        textDecoration: 'line-through',
        fontWeight: '400',
        position: 'absolute',
        top: '0px',
        bottom: '0px',
        right: '72px',
        fontSize: '18px',
        [theme.breakpoints.down('sm')]: {
            right: '50px',
            fontSize: '.8em',
        },
        [theme.breakpoints.down('xs')]: {
            position: 'unset',
            fontSize: '1em',
            fontWeight: '600',
        },
    },

    salePrice: {
        color: theme.palette.pdp.salePrice || '#a1001a',
        margin: '0 0 0 10px',
        fontSize: '20px',
        position: 'absolute',
        right: '0px',
        top: '0px',
    },
    selectedSalePriceInline: {
        position: 'absolute',
        right: '0px',
        top: '0px',
        fontWeight: 'bold',
        fontSize: '20px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '.9em',
        },
        [theme.breakpoints.down('xs')]: {
            position: 'unset',
            fontSize: '1em',
            fontWeight: '700',
        },
    },
    selectedSalePrice: {
        position: 'absolute',
        right: '0px',
        top: '0px',
        fontWeight: 'bold',
        fontSize: '20px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '.9em',
        },
        [theme.breakpoints.down('xs')]: {
            position: 'unset',
            display: 'block',
            fontSize: '1em',
            fontWeight: '700',
        },
    },
    discountAmount: {
        color: theme.palette.cms?.skuPriceDiscountColor,
        fontWeight: 'normal',
        marginLeft: '10px',
        position: 'absolute',
        top: '26px',
        right: '0px',
        fontSize: '18px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '.9em',
        },
        [theme.breakpoints.down('xs')]: {
            position: 'relative',
            top: '0px',
        },
    },

    retailPrice: {
        fontSize: '20px',
        color: theme.palette.common?.black,
    },
    selectedRetailPrice: {
        fontWeight: 'bold',
        color: theme.palette.cms?.pdpPriceColor || theme.palette.productAvailibilityPDP,
        fontSize: '20px',
    },
    productAvailibilityMessage: {
        textTransform: 'uppercase',
        fontSize: '11px',
        fontFamily: theme.typography.fontFamily,
        fontWeight: '700',
        color: theme.palette.productAvailibilityPDP,
        margin: '-2px 0px 0px 0px',
    },
    availabletoShip: {
        textTransform: 'uppercase',
        fontSize: '13px',
        fontFamily: theme.typography.fontFamily,
        fontWeight: '700',
        margin: '-2px 0px 0px 0px',
    },
    productHeaderWrapper: {
        display: 'block',
        position: 'relative',
        padding: 0,
        // Mobile
        [theme.breakpoints.up(768)]: {
            display: 'none',
        },
        // tablet
        [theme.breakpoints.down(768)]: {
            display: 'block',
        },
    },
    titleAndPrice: {
        display: 'flex',
    },
    title: {
        color: theme.palette.black,
        margin: '0',
        fontSize: '1.25em',
        fontWeight: '500',
        position: 'relative',
    },
    upsellNewTitle: {
        color: theme.palette.upsellsTextColor || '#2f2f2f',
        fontWeight: 'bold',
        fontSize: '20px',
        lineHeight: '24px',
    },
    price: {
        textAlign: 'left',
        position: 'relative',
        fontSize: '1em',
        fontWeight: '600',
    },
    productId: {
        margin: '6px 0 8px',
        fontSize: '.8em',
        color: theme.palette.black,
    },
    availability: {
        color: theme.palette.black,
        padding: '10px 0 15px',
        fontSize: '0.9em',
        boxSizing: 'border-box',
        textAlign: 'left',
        fontWeight: '700',
        textTransform: 'capitalize',
    },
    itemSku: {
        fontWeight: 400,
        fontSize: '19px',
        color: theme.palette.black,
    },
    priceBlock: {
        textAlign: 'right',
    },
    shipmentMessage: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '13px',
        lineHeight: '18px',
        textTransform: 'capitalize',
        color: theme.palette.upsellsTextColor || '#2F2F2F',
        textDecoration: 'none',
        paddingTop: '2px',
    },
    shipmentMessageFuture: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '13px',
        lineHeight: '18px',
        textTransform: 'capitalize',
        color: theme.palette.upsellsFutureAvailabilityColor || '#A2011A',
        textDecoration: 'none',
    },
    productSkuNew: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '13px',
        lineHeight: '18px',
        textTransform: 'capitalize',
    },
    priceBox: {
        margin: '6px 0',
    },
    priceAlignment: {
        display: 'flex',
        alignItems: 'flex-end',
    },
    viewPriceBox: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        justifyContent: 'space-between',
    },

    plaProductTitle: {
        padding: '10px 0px',
        fontSize: '18px',
        lineHeight: '26px',
        color: theme.palette.text?.primary || '#2F2F2F',
        fontWeight: 700,
    },
    plaPrice: {
        '& $selectedRetailPrice': {
            paddingBottom: '7px',
            fontSize: '16px',
            lineHeight: '20px',
            color: theme.palette.text?.primary || '#2F2F2F',
        },
    },
    plaAvailablity: {
        paddingTop: '13px',
        '& $productAvailibilityMessage': {
            color: theme.palette.cta4 || '#595959',
            fontSize: '14px',
            lineHeight: '18px',
        },
        '& $availabletoShip': {
            color: theme.palette.cta4 || '#595959',
            fontSize: '14px',
            lineHeight: '18px',
        },
    },
    availabilityMessage: {
        fontSize: '16px',
        lineHeight: '18px',
        textTransform: 'capitalize',
        color: theme.palette.upsellsTextColor || '#2F2F2F',
        margin: '20px 0px 25px',
        '& >div': {
            fontSize: '16px',
            fontFamily: 'LatoBold',
            fontWeight: 'normal',
        },
    },
    productWrapper: {
        [theme.breakpoints.down('600')]: {
            padding: '0px 16px',
        },
        '& $upsellNewTitle , $title': {
            fontSize: '28px',
            lineHeight: '36px',
            letterSpacing: '0.5px',
            marginBottom: '6px',
        },
        '& $productSkuNew': {
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: '0.5px',
        },
        '& $selectedRetailPrice': {
            fontSize: '28px',
            lineHeight: '36px',
            letterSpacing: '0.5px',
        },
        '& $price': {
            margin: '6px 0px 12px',
        },
    },
});

const ProductNameRender = ({
    classes,
    selectedSku,
    product,
    isPassportBundleProduct,
    flagIsAvailableToShipNow,
    flagIsDeliveryMessageNextVariant,
    configuratorProduct,
    productUrl,
    isClubProduct,
    pricesList,
    getSalePrice,
    getRetailPrice,
    isPdpRedesignEnabled,
}) => {
    const ssrDeviceType = useSelector(getSSRDeviceType);
    const { featureFlags, presentation_family: presentationFamily = '' } = useContext(CrossMerch);
    // Removing this design from any feature and abtest
    const newUpsellDesign = presentationFamily === 'food';
    const isShownDiscountAmount = featureFlags['is_dollar_off_shown_enabled'];
    const isAvailabilityAtcLeftAlign = featureFlags['is-availability-atc-left-aligned'];
    const foodPlaRedesign = useSelector(getFeatureFlag('which-food-pla-design'));
    const isPlaPage = useIsPlaPage() && featureFlags['is-simplified-pdp'] && foodPlaRedesign === 'variant';
    const renderSkuPrice = (sku, clubPrice = false) => {
        const round = (value) => Number(`${Math.round(value * 10) / 10}`).toFixed(2);
        const priceNonSelected = clubPrice ? classes.clubPrice : classes.retailPrice;
        const priceSelected = clubPrice ? classes.clubPriceSelected : classes.selectedRetailPrice;
        if (sku?.prices?.[0] && sku?.prices?.[1]) {
            let productPrice = getRetailPrice(pricesList) > 0 ? getRetailPrice(pricesList) : Number((sku.prices[0].value).toFixed(2));
            let discountedProductPrice = getSalePrice(pricesList) > 0 ? getSalePrice(pricesList) : Number((sku.prices[1].value).toFixed(2));
            if (isPassportBundleProduct?.enable) {
                productPrice = Number((productPrice + isPassportBundleProduct?.prices?.value).toFixed(2));
                discountedProductPrice = Number((discountedProductPrice + isPassportBundleProduct?.prices?.value).toFixed(2));
            }
            return productPrice > discountedProductPrice
                ? (
                    <>
                        <s regularprice="">
                            ${productPrice}
                        </s>
                        <span className={selectedSku.id !== sku.id ? classes.salePrice : `${classes.salePrice} ${!newUpsellDesign ? classes.selectedSalePrice : classes.selectedSalePriceInline}`} saleprice="">
                            ${discountedProductPrice}
                        </span>
                        {isShownDiscountAmount && round(productPrice - discountedProductPrice) > 0 && (
                            <span className={classes.discountAmount} discount="">
                                ${round(productPrice - discountedProductPrice)} Off
                            </span>
                        )}
                    </>
                )
                : (
                    <span className={selectedSku.id !== sku.id ? priceNonSelected : priceSelected} productprice="">
                        ${productPrice}
                    </span>

                );
        }
        return null;
    };
    // Starting at the end of the string (backwards pattern matching), continue to match any characters that aren't '-' or '_'
    const itemSku = (id) => (id || '').match(/[^-_]*$/);

    const availabilityMessage = (sku) => {
        const deliveryMessage = sku?.availability?.deliveryMessage || product?.availability?.deliveryMessage || '';
        const dateObject = dateStringReformat(sku?.availability?.earliestShipDate, true) || {};
        const earliestShipDate = new Date(dateObject?.year, dateObject?.month, dateObject?.day);
        const currentDate = new Date();
        if (earliestShipDate > currentDate) {
            return <div className={classes.productAvailibilityMessage}>{flagIsDeliveryMessageNextVariant ? 'NEXT AVAILABLE DATE' : 'Available to Ship'}: {format(earliestShipDate, 'LLL do yyyy')}</div>;
        }
        if (deliveryMessage === 'AVAILABLE TO SHIP NOW') {
            return <div className={classes.availabletoShip}>{deliveryMessage}</div>;
        }
        return <div className={classes.productAvailibilityMessage}>{deliveryMessage}</div>;
    };

    let productName = selectedSku.name;
    if (isPassportBundleProduct?.enable) {
        if (productName?.length < 20) {
            if (isPassportBundleProduct?.skuText) {
                productName = `${productName} ${isPassportBundleProduct?.skuText}`;
            } else {
                productName += ' Bundle';
            }
        }
    }
    // Ensure the name is not updated unnecessarily
    const [transformedName, setTransformedName] = useState(productName);
    useEffect(() => {
        // The name styles kept glitching on hover, this ensures these functions are only called when the name changes
        setTransformedName(transformCharacters(ReactHtmlParser(productName)));
    }, [productName]);

    if (configuratorProduct) {
        return (
            <Grid container xs={12}>
                <Grid item xs={6} className={classes.itemSku}>
                    {itemSku(selectedSku?.id)}
                    {featureFlags['is-pdp-reviews-enabled'] && featureFlags['is-power-review-enabled'] && (
                        <ProductReview
                            partNumber={getBaseCode(product.partNumber)}
                            name={product?.name}
                            productUrl={product?.url}
                            inline
                        />
                    )}
                </Grid>
                <Grid item xs={6} className={classes.priceBlock}>{renderSkuPrice(selectedSku)}</Grid>
                <Grid item xs={12}>
                    {flagIsAvailableToShipNow && !isAvailabilityAtcLeftAlign && (<div className={classes.availability} availability="">{availabilityMessage(selectedSku)}</div>)}
                </Grid>
            </Grid>
        );
    }

    const isShipNow = product?.availability?.deliveryMessage === 'AVAILABLE TO SHIP NOW';
    return (
        <>
            <Media query="(max-width: 600px)" defaultMatches={ssrDeviceType === 'mobile'}>
                {(matches) => (
                    matches && isPlaPage
                        ? (

                            <div className={classes.plaProductHeaderWrapper}>
                                <div className={classes.plaProductTitle}>
                                    {transformedName}
                                </div>
                                <div className={classes.plaPrice}>
                                    {renderSkuPrice(selectedSku)}
                                </div>

                                {!isAvailabilityAtcLeftAlign ? <div className={classes.plaAvailablity} availability="">{availabilityMessage(selectedSku)}</div> : <></>}
                                {featureFlags['is-pdp-reviews-enabled'] && featureFlags['is-power-review-enabled'] && (
                                    <ProductReview
                                        product={product}
                                        selectedSku={selectedSku}
                                        productUrl={productUrl}
                                        isClubProduct={isClubProduct}
                                        showUnderTitle
                                        inline={isAvailabilityAtcLeftAlign}
                                    />
                                )}
                            </div>

                        )
                        : (
                            <div className={`${classes.productHeaderWrapper} ${isPdpRedesignEnabled ? classes.productWrapper : ''}`}>
                                <div className={newUpsellDesign ? classes.upsellNewTitle : classes.title}>
                                    {transformedName}
                                    {ssrDeviceType === 'mobile' && featureFlags['is-pdp-reviews-enabled'] && featureFlags['is-power-review-enabled'] && (
                                        <ProductReview
                                            product={product}
                                            selectedSku={selectedSku}
                                            productUrl={productUrl}
                                            isClubProduct={isClubProduct}
                                            showUnderTitle
                                            inline={isAvailabilityAtcLeftAlign}
                                            isPdpRedesignEnabled={isPdpRedesignEnabled}
                                        />
                                    )}
                                </div>
                                {featureFlags['is-pdp-qty-availability-enabled'] && (<div className={classes.availabilityMessage}>{availabilityMessage(selectedSku)}</div>)}
                                {!newUpsellDesign && <div className={classes.productId}>{itemSku(selectedSku?.id)}</div>}
                                {newUpsellDesign && (
                                    <Grid container className={classes.priceBox}>
                                        <Grid className={classes.priceAlignment} item lg={3} md={3} sm={3} xl={3} xs={3}>
                                            <div className={classes.productSkuNew}>{getBaseCode(selectedSku.id, true)}</div>
                                        </Grid>
                                        {!isAvailabilityAtcLeftAlign ? (
                                            <Grid item lg={9} md={9} sm={9} xl={9} xs={9}>
                                                <div className={`${classes.shipmentMessage} ${!isShipNow ? classes.shipmentMessageFuture : ''}`} availability="">{product?.availability?.deliveryMessage?.toLowerCase?.()}</div>
                                            </Grid>
                                        ) : ''}
                                    </Grid>
                                )}
                                {newUpsellDesign ? (
                                    <div className={classes.viewPriceBox}>
                                        <div className={classes.price}>
                                            {renderSkuPrice(selectedSku)}
                                        </div>
                                    </div>
                                )
                                    : (
                                        <div className={classes.price}>
                                            {renderSkuPrice(selectedSku)}
                                        </div>
                                    )}
                                {ssrDeviceType === 'mobile' && !newUpsellDesign && (
                                    <KlarnaCreditPromotionBadge
                                        selectedSku={selectedSku}
                                    />
                                )}
                                {!newUpsellDesign && flagIsAvailableToShipNow && !isAvailabilityAtcLeftAlign && (<div className={classes.availability} availability="">{availabilityMessage(selectedSku)}</div>)}
                            </div>
                        ))}
            </Media>
        </>
    );
};

ProductNameRender.propTypes = {
    classes: object.isRequired,
    selectedSku: shape({
        id: string.isRequired,
        name: string.isRequired,
        prices: arrayOf(
            shape({
                type: string.isRequired,
                value: number.isRequired,
                __typename: string.isRequired,
            }),
        ).isRequired,
        __typename: string.isRequired,
    }).isRequired,
    product: shape({
        name: string.isRequired,
        partNumber: string.isRequired,
        shortDescription: string.isRequired,
        image: shape({
            name: string.isRequired,
            path: string.isRequired,
        }).isRequired,
        availability: shape({
            deliveryMessage: string.isRequired,
            productDeliveryType: string.isRequired,
        }).isRequired,
    }).isRequired,
    isPassportBundleProduct: shape({
        prices: {
            value: number,
        },
        enable: bool,
    }),
    flagIsAvailableToShipNow: bool,
    flagIsDeliveryMessageNextVariant: bool,
    configuratorProduct: bool,
    productUrl: string,
    isClubProduct: bool,
    pricesList: array,
    getSalePrice: func,
    getRetailPrice: func,
    isPdpRedesignEnabled: bool,
};

ProductNameRender.defaultProps = {
    isPassportBundleProduct: {},
    flagIsAvailableToShipNow: false,
    flagIsDeliveryMessageNextVariant: false,
    configuratorProduct: false,
    productUrl: '',
    isClubProduct: false,
    pricesList: [],
    getSalePrice: () => {},
    getRetailPrice: () => {},
    isPdpRedesignEnabled: false,
};
const enhance = compose(
    withStyles(styles),
);

export default enhance(ProductNameRender);
